import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Tooltip } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { FiCheck } from "react-icons/fi";
import { Link, Sms, Message, Video } from "iconsax-react";

import { Color } from "../../../../../components/Form/Button";
import Checkbox from "../../../../../components/Form/Checkbox";
import { ActionButtonProps } from "../../../../../components/Generic/ActionButtons";

import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import LoadingModal from "../../../../../components/LoadingModal";
import { useActionQuote } from "../../../../../hooks/useActionQuote";
import { Customer } from "../../../../../models/customer";
import { Sales } from "../../../../../models/sales";
import { Task } from "../../../../../models/task";
import EstimateService from "../../../../../services/estimate.service";
import TaskService from "../../../../../services/task.service";
import { ShareOptions, UsedInEmailTemplates } from "../../../../../util/enums";
import {
  Card,
  CardBody,
  CardLoading,
  CardText,
  CardTitle,
  LoadingContainer,
  StylishModal,
  TaskCard,
  TaskInfo,
  TaskOptions,
  Wrapper,
  WrapperCardBody,
} from "./styles";
import EmailEditor, {
  EmailEditorHandle,
} from "Components/Generic/SendCommunicationModal/components/EmailEditor";
import SMSEditor, {
  SMSEditorHandle,
} from "Components/Generic/SendCommunicationModal/components/SMSEditor";

interface SendToCustomerProps {
  tasks?: Task[];
  onRefetch?: () => void;
}

export interface SendToCustomerHandle {
  onOpen: (jobId: number, quote: Sales, customer?: Customer) => void;
}

type Field = "sendToEmail" | "sendToSms";

const SendToCustomerModal: ForwardRefRenderFunction<SendToCustomerHandle, SendToCustomerProps> = (
  props,
  ref
) => {
  const { t: translate } = useTranslation();
  const { tasks, onRefetch } = props;
  const { sendToCustomer, sendToCustomerLoading } = useActionQuote();

  const [open, setOpen] = useState<boolean>(false);
  const [hasEstimate, setHasEstimate] = useState<boolean>(false);
  const [jobId, setJobId] = useState<number>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [tasksToSend, setTasksToSend] = useState<Task[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [customer, setCustomer] = useState<Customer>();
  const [quote, setQuote] = useState<Sales>();

  const smsEditorRef = useRef<SMSEditorHandle>(null);
  const emailEditorRef = useRef<EmailEditorHandle>(null);

  useImperativeHandle(ref, () => ({
    onOpen: onOpenModal,
  }));

  const { isFetching: isLoadingEstimate } = useQuery(
    ["estimate.item", jobId],
    () => EstimateService.getByJobId(jobId),
    {
      enabled: !!jobId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          setHasEstimate(true);
        }
      },
    }
  );

  const { mutateAsync: updateTask, isLoading } = useMutation(
    ["task.video.survey.customer.notification"],
    ({ id, task }: { id: number; task: Task }) =>
      TaskService.sendVideoSurveyNotificationToCustomer(id, task)
  );

  const hasTaskToSend =
    tasksToSend.some((taskToSend) => taskToSend.sendToEmail) ||
    tasksToSend.some((taskToSend) => taskToSend.sendToSms);

  const isSaveButtonDisabled =
    (currentStep === 0 && !selectedOption.trim().length) ||
    (currentStep && !hasTaskToSend) ||
    isLoading;

  const actionButtonProps: ActionButtonProps = {
    dataCySave: "submitButton",
    dataCyCancel: "cancelButton",
    textSave: translate("share"),
    textCancel: currentStep ? translate("back") : translate("cancel"),
    customColorSave: Color.SUCCESS,
    customColorCancel: currentStep ? Color.TEXT_INFO : Color.CANCEL,
    saveDisabled: isSaveButtonDisabled,
    onSave: () => onSaveAction(),
    onCancel:
      currentStep ||
      selectedOption === ShareOptions.SHARE_EMAIL ||
      selectedOption === ShareOptions.SHARE_SMS
        ? () => {
            setCurrentStep(0);
            setSelectedOption("");
          }
        : () => {
            setOpen(false);
            setSelectedOption("");
          },
  };

  const onReturnIfTaskShouldSend = (taskId: number, field: Field): boolean => {
    return !!tasksToSend.find((taskToSend) => taskToSend.id === taskId)?.[field];
  };

  const onSaveAction = () => {
    if (currentStep && !isSaveButtonDisabled) {
      return onUpdateTask();
    }

    if (selectedOption === ShareOptions.SHARE_ESTIMATE_LINK)
      return sendToCustomer(Number(jobId), onCloseModal);

    if (selectedOption === ShareOptions.SHARE_EMAIL) {
      return emailEditorRef.current?.sendEmail();
    }

    if (selectedOption === ShareOptions.SHARE_SMS) {
      return smsEditorRef.current?.sendSMS();
    }

    return null;
  };

  const onOpenModal = (jobId: number, quote: Sales, customerObj?: Customer) => {
    setOpen(true);
    setJobId(jobId);
    setQuote(quote);
    setCustomer(customerObj);
  };

  const onCloseModal = () => {
    setTasksToSend([]);
    setCurrentStep(0);
    setSelectedOption("");
    if (onRefetch) onRefetch();
    setOpen(false);
  };

  const onChangeTask = (updatedTask: Task, checked: boolean, field: Field) => {
    if (!tasks?.length) return;

    if (tasksToSend.length) {
      const alreadyAddedTaskIndex = tasksToSend.findIndex((task) => task.id === updatedTask.id);

      if (alreadyAddedTaskIndex > -1) {
        const updatedTasks = [...tasksToSend];
        const task = updatedTasks[alreadyAddedTaskIndex];

        task[field] = checked;
        updatedTasks[alreadyAddedTaskIndex] = task;

        setTasksToSend(updatedTasks);
      } else {
        updatedTask[field] = checked;

        setTasksToSend((state) => [...state, updatedTask]);
      }
    } else {
      updatedTask[field] = checked;

      setTasksToSend((state) => [...state, updatedTask]);
    }
  };

  const onUpdateTask = async () => {
    const responses: AxiosResponse<boolean>[] = [];

    await Promise.all(
      tasksToSend.map(async (task) => {
        if (task.id) {
          const response = await updateTask({ id: task.id, task });

          responses.push(response);
        }
      })
    );

    const hasError = responses.find((response) => response.status !== 200);

    if (hasError) NotificationToast.error(translate("anErrorOccurredTryAgain"));
    else NotificationToast.success(translate("theInvitationWasSuccessfullySent"));

    onCloseModal();
  };

  const disableVideoSurveyOption = !tasks?.length;

  const shareOptions = [
    {
      icon: <Video size="25" />,
      title: translate("shareViaVideoSurveyInvite"),
      text: translate("sendANewInvitationToTheScheduledVideoSurvey"),
      disabled: disableVideoSurveyOption,
      action: disableVideoSurveyOption
        ? undefined
        : () => {
            setCurrentStep(1);
            setSelectedOption("");
          },
    },
    {
      icon: <Link size="25" />,
      key: ShareOptions.SHARE_ESTIMATE_LINK,
      title: translate("shareViaEstimatedQuoteLink"),
      text: translate("sendTheExternalLinkOfEstimatedQuote"),
      disabled: !hasEstimate,
      isLoading: isLoadingEstimate,
      action: hasEstimate
        ? () => {
            setSelectedOption(ShareOptions.SHARE_ESTIMATE_LINK);
          }
        : undefined,
    },
    {
      icon: <Message size="25" />,
      key: ShareOptions.SHARE_EMAIL,
      title: translate("sendEmail"),
      text: translate("writeAnEmailOrUseATemplate"),
      action: () => setSelectedOption(ShareOptions.SHARE_EMAIL),
    },
    {
      icon: <Sms size="24" variant="Outline" />,
      key: ShareOptions.SHARE_SMS,
      title: translate("sendSMS") || "Send SMS",
      text: translate("writeASMSOrUseATemplate") || "Write a SMS or use a template",
      tooltipTitleOnDisabled: translate(""),
      disabled: false,
      action: () => setSelectedOption(ShareOptions.SHARE_SMS),
    },
  ];

  const getOptionTitle = (optionKey?: string) => {
    if (optionKey === ShareOptions.SHARE_ESTIMATE_LINK) {
      return isLoadingEstimate
        ? translate("weAreCheckingIfALinkIsAvailable")
        : translate("thisQuoteDoesNotHaveAnEstimate");
    }

    return translate("thisQuoteDoesNotHaveAValidVideoSurveyTask");
  };

  const renderShareOptions = () => {
    return shareOptions.map((option, index: number) => (
      <Tooltip
        key={`option-${index + 1}`}
        title={option.disabled ? getOptionTitle(option.key) : ""}
        placement="top"
      >
        <Card
          disabled={option.disabled}
          active={selectedOption === option.key}
          onClick={option.action}
        >
          {option.icon}
          <WrapperCardBody>
            <CardBody>
              <CardTitle>{option.title}</CardTitle>
              <CardText>{option.text}</CardText>
            </CardBody>
            {selectedOption === option.key ? <FiCheck size="18" /> : null}
          </WrapperCardBody>
          {option?.isLoading && (
            <LoadingContainer>
              <CardLoading size={20} />
            </LoadingContainer>
          )}
        </Card>
      </Tooltip>
    ));
  };

  const renderTaskOptions = () => {
    return tasks?.map((task, index) => (
      <TaskCard key={index}>
        <TaskInfo>
          <span>
            {task.type} {tasks.length > 1 ? `(${index + 1})` : ""}
          </span>
          <span>({format(new Date(task.startDate), "dd/MM HH:mm a")})</span>
        </TaskInfo>
        <TaskOptions>
          <Checkbox
            name="email"
            label={translate("email")}
            checked={!!task.sendToEmail || onReturnIfTaskShouldSend(Number(task.id), "sendToEmail")}
            disabled={isLoading}
            onChange={() => onChangeTask(task, !task.sendToEmail, "sendToEmail")}
          />
          <Checkbox
            name="sms"
            label={translate("sms")}
            checked={!!task.sendToSms || onReturnIfTaskShouldSend(Number(task.id), "sendToSms")}
            disabled={isLoading}
            onChange={() => onChangeTask(task, !task.sendToSms, "sendToSms")}
          />
        </TaskOptions>
      </TaskCard>
    ));
  };

  const modalTitle = currentStep
    ? translate("wouldYouLikeToSendTheDetailsOfTheCallToYourCustomerBy")
    : translate("whatDoYouWantToSendToCustomer");

  return (
    <StylishModal
      open={open}
      handleClose={() => {
        setSelectedOption("");
        setCurrentStep(0);
        setHasEstimate(false);
        setJobId(undefined);
        setOpen(false);
      }}
      showActionButtons
      title={modalTitle}
      aria-labelledby="simple-modal-title"
      aria-describedby="simple-modal-description"
      actionButtonProps={actionButtonProps}
    >
      <LoadingModal showing={sendToCustomerLoading || isLoading} />
      {selectedOption !== ShareOptions.SHARE_EMAIL && selectedOption !== ShareOptions.SHARE_SMS ? (
        <Wrapper>{currentStep ? renderTaskOptions() : renderShareOptions()}</Wrapper>
      ) : null}

      {selectedOption === ShareOptions.SHARE_EMAIL ? (
        <EmailEditor
          ref={emailEditorRef}
          usedIn={UsedInEmailTemplates.QUOTES}
          onSend={() => {
            setSelectedOption("");
            setCurrentStep(0);
            setHasEstimate(false);
            setJobId(undefined);
            setOpen(false);
          }}
          onCancel={() => {
            setCurrentStep(0);
            setSelectedOption("");
          }}
          entityContext={{
            entityId: jobId!,
            entityType: "quote",
            entity: quote!,
            customer: quote?.customer,
          }}
        />
      ) : null}

      {selectedOption === ShareOptions.SHARE_SMS ? (
        <SMSEditor
          ref={smsEditorRef}
          entityContext={{
            entityId: jobId!,
            entityType: "quote",
            entity: quote!,
            customer: quote?.customer,
          }}
          usedIn={UsedInEmailTemplates.QUOTES}
          onSend={() => {
            setSelectedOption("");
            setCurrentStep(0);
            setHasEstimate(false);
            setJobId(undefined);
            setOpen(false);
          }}
          onCancel={() => {
            setCurrentStep(0);
            setSelectedOption("");
          }}
        />
      ) : null}
    </StylishModal>
  );
};

export default forwardRef(SendToCustomerModal);
